import { ApolloError, useMutation } from '@apollo/client';
import { Button, Input, palettes, useFlashMessage } from '@indigo-ag/component-module';
import { AgFinancingCreditApprovalStatus, CreditApprovalData, OpportunityType, Phase2 } from '@indigo-ag/schema';
import React, { useContext, useState } from 'react';

import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import i18next from 'core/i18n';
import { currency, CURRENCY_SYMBOL, utils } from 'shared';
import { UserContext } from 'core/components/GateWay/GateWay';
import {
  createCreditApprovalRound,
  getCreditApprovalData,
  setApprovedCreditAmount
} from 'features/brazil-grower/queries/creditApproval.queries';
import { financingAgFinancing } from 'features/brazil-grower/queries';

interface Props {
  creditApprovalData?: CreditApprovalData;
  creditApprovalStatus?: Phase2['creditApprovalStatus'];
}

export const StartCreditApproval = ({ creditApprovalData, creditApprovalStatus }: Props) => {
  /**
   * Context
   */
  const { agFinancingId, agFinance } = useContext(FinanceProcessContext);
  const { isCreditAnalyst } = useContext(UserContext);

  /**
   * Mutations
   */
  const [createCreditApprovalRoundFn, { loading: loadingCreateCreditApprovalRound }] = useMutation(
    createCreditApprovalRound
  );
  const [setApprovedCreditAmountFn, { loading: loadingSetApprovedCreditAmount }] = useMutation(setApprovedCreditAmount);

  const { addFlashMessage } = useFlashMessage();

  const approvedLimitFormatted = agFinance?.phase2?.approvedCreditAmount
    ? currency.inputMaskCurrency(agFinance?.phase2?.approvedCreditAmount, { currencySymbol: undefined })
    : '0,00';
  const [approvedLimit, setApprovedLimit] = useState<string>(String(approvedLimitFormatted));

  // Round already exists or not credit analyst (who start the round)
  if (
    (agFinance?.opportunityType === OpportunityType.BARTER_GUARANTEE && !!creditApprovalData?.round) ||
    (agFinance?.opportunityType !== OpportunityType.BARTER_GUARANTEE &&
      (!!creditApprovalData?.round || !isCreditAnalyst))
  ) {
    return null;
  }

  // Credit approval status should be IN_PROGRESS - initial status set after external credit analysis is complete
  if (
    agFinance?.opportunityType !== OpportunityType.BARTER_GUARANTEE &&
    (!creditApprovalStatus || creditApprovalStatus !== AgFinancingCreditApprovalStatus.IN_PROGRESS)
  ) {
    return null;
  }

  const startCreditApproval = async () => {
    let approvedCreditAmountFormatted = undefined;

    if (agFinance?.phase2?.id) {
      approvedCreditAmountFormatted = Number(approvedLimit.replace(/\./g, '').replace(/,/g, '.'));
      await setApprovedCreditAmountFn({
        variables: {
          phase2Id: agFinance?.phase2?.id,
          approvedCreditAmount: approvedCreditAmountFormatted
        },
        awaitRefetchQueries: true
      });
    }

    const decisionSheetDataFormatted = utils.graphql.removeTypename(creditApprovalData?.decisionSheet);
    return createCreditApprovalRoundFn({
      variables: {
        creditApprovalRoundInput: {
          decisionSheet: {
            ...decisionSheetDataFormatted,
            approvedCreditAmount: approvedCreditAmountFormatted,
            lawsuits: decisionSheetDataFormatted?.lawsuits
              ? utils.graphql.removeTypename(decisionSheetDataFormatted?.lawsuits)
              : undefined,
            debts: decisionSheetDataFormatted?.debts
              ? utils.graphql.removeTypename(decisionSheetDataFormatted?.debts)
              : undefined,
            priorLoans: decisionSheetDataFormatted?.priorLoans
              ? utils.graphql.removeTypename(decisionSheetDataFormatted?.priorLoans)
              : undefined,
            exposurePerCrop: decisionSheetDataFormatted?.exposurePerCrop
              ? utils.graphql.removeTypename(decisionSheetDataFormatted?.exposurePerCrop)
              : undefined,
            cropCashFlows: decisionSheetDataFormatted?.cropCashFlows
              ? utils.graphql.removeTypename(decisionSheetDataFormatted?.cropCashFlows)
              : undefined
          },
          referenceId: agFinancingId
        }
      },
      refetchQueries: [
        {
          query: getCreditApprovalData,
          variables: { agFinancingId }
        },
        {
          query: financingAgFinancing,
          variables: { agFinancingId }
        }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        addFlashMessage({
          content: '',
          title: utils.text.toTitleCase(i18next.t('creditApprovalStarted')),
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>): void =>
    setApprovedLimit(currency.inputMaskCurrency(event.target.value, { currencySymbol: undefined }));

  const renderBrazilCurrencySymbol = (): JSX.Element => (
    <span style={{ color: palettes.neutral['900'] }}>{CURRENCY_SYMBOL.BR}</span>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      <Input
        label={i18next.t('approvedLimit')}
        onChange={handleLimitChange}
        renderBeforeInput={renderBrazilCurrencySymbol}
        value={approvedLimit}
        variant="text"
      />
      <Button
        onClick={startCreditApproval}
        size="small"
        busy={loadingCreateCreditApprovalRound || loadingSetApprovedCreditAmount}
        disabled={approvedLimit === '0,00'}
      >
        {i18next.t('start')}
      </Button>
    </div>
  );
};
